import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth, useTheme, API } from '../App';
import { toast } from 'sonner';
import { 
  CircleDot, Sun, Moon, ArrowLeft, ArrowRight, Coins, Wand2,
  Hexagon, Circle, Triangle, Star, Palette, Grid3X3, Sparkles,
  Cpu, ImageIcon, Zap
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Slider
} from '../components/ui/slider';
import TermsOfUseModal from '../components/TermsOfUseModal';

const TRADITIONS = [
  { value: 'vedic', label: 'Vedic', icon: '🕉️', description: 'Ancient Hindu/Buddhist patterns' },
  { value: 'hermetic', label: 'Hermetic', icon: '⚗️', description: 'Western esoteric alchemy' },
  { value: 'minimalist', label: 'Minimalist', icon: '○', description: 'Clean, essential forms' },
  { value: 'art_nouveau', label: 'Art Nouveau', icon: '🌿', description: 'Organic curves & nature' },
  { value: 'sci_fi_mystic', label: 'Sci-Fi Mystic', icon: '🌌', description: 'Cosmic technology' },
];

const GEOMETRIES = [
  { value: 'seed_of_life', label: 'Seed of Life', description: '7 circles - φ ratio construction' },
  { value: 'flower_of_life', label: 'Flower of Life', description: 'Universal life patterns' },
  { value: 'golden_ratio', label: 'Golden Spiral', description: 'Fibonacci φ spirals' },
  { value: 'metatron', label: "Metatron's Cube", description: '13 circles + platonic solids' },
  { value: 'vesica_piscis', label: 'Vesica Piscis', description: 'Sacred intersection geometry' },
  { value: 'yin_yang', label: 'Yin Yang', description: 'Boolean duality (φ² eyes)' },
  { value: 'sri_yantra', label: 'Sri Yantra', description: 'Interlocking triangles' },
  { value: 'sigil_core', label: 'Sigil Core (φ Reference)', description: 'Mathematical precision sigil: r=100, R=φ×r' },
  { value: 'sigil_hexagonal', label: 'Sigil Hexagonal', description: '6-fold sigil with duality center' },
  { value: 'nested_mandala', label: 'Nested Mandala', description: 'φ-layered concentric patterns' },
  { value: 'torus_knot', label: 'Torus Knot', description: 'Mathematical knot projection' },
];

const SYMMETRIES = [
  { value: 4, label: '4-Fold (Square)', description: 'Elements & directions • Uses squares, octagons' },
  { value: 6, label: '6-Fold (Hexagonal)', description: 'Harmony & hex-grid • Uses hexagons, 12-gons' },
  { value: 8, label: '8-Fold (Octagonal)', description: 'Infinity & manifestation • Uses octagons, 16-gons' },
  { value: 12, label: '12-Fold (Zodiac)', description: 'Zodiac & completion • Uses dodecagons, 24-gons' },
];

const PALETTES = [
  { value: 'cosmic', label: 'Cosmic', colors: ['#4169e1', '#9370db', '#ffd700'] },
  { value: 'vedic', label: 'Vedic', colors: ['#c41e3a', '#ff6b35', '#ffd700'] },
  { value: 'hermetic', label: 'Hermetic', colors: ['#4a0080', '#7b68ee', '#ffd700'] },
  { value: 'earthy', label: 'Earthy', colors: ['#8b4513', '#228b22', '#daa520'] },
  { value: 'ethereal', label: 'Ethereal', colors: ['#b0c4de', '#add8e6', '#c0c0c0'] },
  { value: 'sci_fi_mystic', label: 'Sci-Fi', colors: ['#00ffff', '#0080ff', '#ff00ff'] },
  { value: 'monochrome', label: 'Monochrome', colors: ['#1a1a1a', '#808080', '#ffffff'] },
];

const INTENTIONS = [
  'Grounding', 'New Beginnings', 'Heart Opening', 'Protection',
  'Abundance', 'Clarity', 'Healing', 'Transformation',
  'Inner Peace', 'Creative Flow', 'Spiritual Connection', 'Balance'
];

const GENERATION_MODES = [
  { 
    value: 'vector', 
    label: 'Vector (Instant)', 
    icon: <Cpu className="w-5 h-5" />,
    description: 'Mathematically precise SVG using φ ratio. Instant generation, perfect for print & laser cutting.',
    badge: 'Recommended'
  },
  { 
    value: 'ai', 
    label: 'AI Art (60s)', 
    icon: <ImageIcon className="w-5 h-5" />,
    description: 'OpenAI GPT Image generation. More artistic interpretation, takes ~60 seconds.',
    badge: null
  },
];

const OUTPUT_PROFILES = [
  {
    value: 'sigil',
    label: 'Sigil (Logo)',
    icon: <Zap className="w-5 h-5" />,
    description: 'Bold hierarchical strokes using φ ratio. High contrast, minimal scaffolding. Perfect for logos, icons, and brand marks.',
    features: ['φ-based stroke weights', 'High contrast colors', 'Construction lines hidden', 'Print & laser ready']
  },
  {
    value: 'blueprint',
    label: 'Blueprint (Technical)',
    icon: <Grid3X3 className="w-5 h-5" />,
    description: 'Hairline technical strokes with multi-colored layers. Full construction visible. Perfect for study and documentation.',
    features: ['Uniform 1px strokes', 'Layer-colored elements', 'All scaffolding visible', 'Mathematical clarity']
  }
];

const STROKE_LINECAPS = [
  { value: 'round', label: 'Round', description: 'Smooth rounded ends' },
  { value: 'butt', label: 'Butt', description: 'Flat cut ends' },
  { value: 'square', label: 'Square', description: 'Extended square ends' },
];

const STROKE_LINEJOINS = [
  { value: 'round', label: 'Round', description: 'Smooth rounded corners' },
  { value: 'miter', label: 'Miter', description: 'Sharp pointed corners' },
  { value: 'bevel', label: 'Bevel', description: 'Flat cut corners' },
];

const Studio = () => {
  const { user, token, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [presets, setPresets] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(() => {
    // Check if user has previously accepted terms in this session
    return sessionStorage.getItem('mandalamuse_terms_accepted') === 'true';
  });
  
  // Form state
  const [intention, setIntention] = useState('');
  const [tradition, setTradition] = useState('vedic');
  const [geometry, setGeometry] = useState('flower_of_life');
  const [symmetry, setSymmetry] = useState(6);
  const [palette, setPalette] = useState('cosmic');
  const [generationMode, setGenerationMode] = useState('vector'); // vector or ai
  const [outputProfile, setOutputProfile] = useState('sigil'); // sigil or blueprint
  const [strokeLinecap, setStrokeLinecap] = useState('round');
  const [strokeLinejoin, setStrokeLinejoin] = useState('round');

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      const res = await fetch(`${API}/presets`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setPresets(data);
      }
    } catch (err) {
      console.error('Failed to fetch presets');
    }
  };

  const loadPreset = (preset) => {
    setIntention(preset.intention);
    setTradition(preset.tradition);
    setGeometry(preset.geometry_framework);
    setSymmetry(preset.symmetry_type);
    setPalette(preset.color_palette);
    toast.success(`Loaded preset: ${preset.name}`);
  };

  // Called when user clicks Generate button
  const handleGenerateClick = () => {
    if (!intention.trim()) {
      toast.error('Please enter your intention on Step 1 before generating');
      return;
    }

    if ((user?.credits || 0) < 1) {
      toast.error('Insufficient credits');
      navigate('/credits');
      return;
    }

    // If user hasn't accepted terms this session, show the modal
    if (!hasAcceptedTerms) {
      setShowTermsModal(true);
      return;
    }

    // Otherwise proceed with generation
    handleGenerate();
  };

  // Called after terms are accepted
  const handleTermsAccepted = () => {
    setHasAcceptedTerms(true);
    sessionStorage.setItem('mandalamuse_terms_accepted', 'true');
    setShowTermsModal(false);
    // Proceed with generation
    handleGenerate();
  };

  const handleTermsDeclined = () => {
    setShowTermsModal(false);
    toast.info('You must accept the Terms of Use to generate artwork');
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    const startTime = Date.now();

    try {
      const res = await fetch(`${API}/artworks/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          intention,
          tradition,
          geometry_framework: geometry,
          symmetry_type: symmetry,
          color_palette: palette,
          print_size: 'square',
          generation_mode: generationMode,
          output_profile: outputProfile,
          stroke_linecap: strokeLinecap,
          stroke_linejoin: strokeLinejoin,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Generation failed');
      }

      const artwork = await res.json();
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
      updateUser({ credits: (user?.credits || 0) - 1 });
      toast.success(`Sacred geometry created in ${elapsed}s!`);
      navigate(`/artwork/${artwork.artwork_id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const totalSteps = 4;

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background overlay when generating */}
      <AnimatePresence>
        {isGenerating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/90 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 relative">
                <div className="absolute inset-0 border-4 border-primary/30 rounded-full animate-ping" />
                <div className="absolute inset-2 border-4 border-accent/30 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
                <div className="absolute inset-4 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
              <h2 className="font-heading text-2xl mb-2">
                {generationMode === 'vector' ? 'Calculating Sacred Geometry' : 'Creating Sacred Geometry'}
              </h2>
              <p className="text-muted-foreground">
                {generationMode === 'vector' 
                  ? 'Applying φ ratio constraints and polar symmetry...'
                  : 'Channeling your intention into form...'}
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                {generationMode === 'vector' ? 'Almost instant...' : 'This may take up to 60 seconds'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 border-b border-border bg-background/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Gallery</span>
          </Link>

          <div className="flex items-center gap-2">
            <CircleDot className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl">Creation Studio</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Coins className="w-4 h-4 text-primary" />
              <span className="font-mono" data-testid="studio-credits">{user?.credits || 0}</span>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              data-testid="studio-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Progress */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Step {step} of {totalSteps}</span>
              <span className="text-sm text-muted-foreground">
                {step === 1 && 'Set Intention'}
                {step === 2 && 'Choose Style'}
                {step === 3 && 'Configure Geometry'}
                {step === 4 && 'Generation Mode'}
              </span>
            </div>
            <div className="h-1 bg-secondary rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-primary to-accent"
                initial={{ width: '0%' }}
                animate={{ width: `${(step / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Step 1: Intention */}
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center mb-10">
                  <h1 className="font-heading text-4xl mb-4" data-testid="step1-title">What is your intention?</h1>
                  <p className="text-muted-foreground">
                    The sacred geometry will be infused with this energy
                  </p>
                </div>

                {/* Quick intentions */}
                <div className="flex flex-wrap gap-2 justify-center mb-6">
                  {INTENTIONS.map((i) => (
                    <Button
                      key={i}
                      variant={intention.toLowerCase() === i.toLowerCase() ? 'default' : 'outline'}
                      size="sm"
                      className="rounded-full"
                      onClick={() => setIntention(i)}
                      data-testid={`intention-${i.toLowerCase().replace(' ', '-')}`}
                    >
                      {i}
                    </Button>
                  ))}
                </div>

                <div className="glass-card p-8">
                  <Label htmlFor="intention" className="text-lg mb-4 block">Or write your own intention</Label>
                  <Textarea
                    id="intention"
                    placeholder="e.g., Manifest abundance and creative flow in my work..."
                    value={intention}
                    onChange={(e) => setIntention(e.target.value)}
                    className="min-h-[120px] text-lg"
                    data-testid="intention-input"
                  />
                </div>

                {/* Presets */}
                {presets.length > 0 && (
                  <div className="glass-card p-6">
                    <h3 className="font-heading text-lg mb-4">Load from saved presets</h3>
                    <div className="flex flex-wrap gap-2">
                      {presets.map((preset) => (
                        <Button
                          key={preset.preset_id}
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => loadPreset(preset)}
                          data-testid={`load-preset-${preset.preset_id}`}
                        >
                          {preset.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 2: Tradition/Style */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center mb-10">
                  <h1 className="font-heading text-4xl mb-4" data-testid="step2-title">Choose your tradition</h1>
                  <p className="text-muted-foreground">
                    Select the aesthetic style for your sacred geometry
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {TRADITIONS.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setTradition(t.value)}
                      className={`glass-card p-6 text-left transition-all duration-300 ${
                        tradition === t.value 
                          ? 'border-primary ring-2 ring-primary/20' 
                          : 'hover:border-primary/50'
                      }`}
                      data-testid={`tradition-${t.value}`}
                    >
                      <span className="text-3xl mb-4 block">{t.icon}</span>
                      <h3 className="font-heading text-xl mb-1">{t.label}</h3>
                      <p className="text-sm text-muted-foreground">{t.description}</p>
                    </button>
                  ))}
                </div>

                <div className="glass-card p-6">
                  <Label className="text-lg mb-4 block">Color Palette</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {PALETTES.map((p) => (
                      <button
                        key={p.value}
                        onClick={() => setPalette(p.value)}
                        className={`p-4 rounded-xl border transition-all ${
                          palette === p.value 
                            ? 'border-primary ring-2 ring-primary/20' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`palette-${p.value}`}
                      >
                        <div className="flex gap-1 mb-2 justify-center">
                          {p.colors.map((color, i) => (
                            <div
                              key={i}
                              className="w-6 h-6 rounded-full border border-white/20"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <span className="text-sm">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Geometry */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center mb-10">
                  <h1 className="font-heading text-4xl mb-4" data-testid="step3-title">Configure geometry</h1>
                  <p className="text-muted-foreground">
                    Fine-tune the sacred framework and symmetry
                  </p>
                </div>

                <div className="glass-card p-6">
                  <Label className="text-lg mb-4 block">Geometry Framework</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {GEOMETRIES.map((g) => (
                      <button
                        key={g.value}
                        onClick={() => setGeometry(g.value)}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          geometry === g.value 
                            ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`geometry-${g.value}`}
                      >
                        <h3 className="font-heading text-lg mb-1">{g.label}</h3>
                        <p className="text-sm text-muted-foreground">{g.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="glass-card p-6">
                  <Label className="text-lg mb-4 block">Symmetry Type</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {SYMMETRIES.map((s) => (
                      <button
                        key={s.value}
                        onClick={() => setSymmetry(s.value)}
                        className={`p-4 rounded-xl border text-center transition-all ${
                          symmetry === s.value 
                            ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`symmetry-${s.value}`}
                      >
                        <span className="text-2xl font-heading block mb-1">{s.value}</span>
                        <span className="text-sm text-muted-foreground">{s.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Generation Mode */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center mb-10">
                  <h1 className="font-heading text-4xl mb-4" data-testid="step4-title">Choose Generation Mode</h1>
                  <p className="text-muted-foreground">
                    Vector mode uses mathematical precision (φ ratio), AI mode creates artistic interpretations
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {GENERATION_MODES.map((mode) => (
                    <button
                      key={mode.value}
                      onClick={() => setGenerationMode(mode.value)}
                      className={`glass-card p-8 text-left transition-all duration-300 relative ${
                        generationMode === mode.value 
                          ? 'border-primary ring-2 ring-primary/20' 
                          : 'hover:border-primary/50'
                      }`}
                      data-testid={`mode-${mode.value}`}
                    >
                      {mode.badge && (
                        <span className="absolute -top-2 right-4 px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                          {mode.badge}
                        </span>
                      )}
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 text-primary">
                        {mode.icon}
                      </div>
                      <h3 className="font-heading text-xl mb-2">{mode.label}</h3>
                      <p className="text-sm text-muted-foreground">{mode.description}</p>
                    </button>
                  ))}
                </div>

                {/* Output Profile (Vector Mode Only) */}
                {generationMode === 'vector' && (
                  <div className="space-y-4">
                    <div className="text-center">
                      <h2 className="font-heading text-xl mb-2">Output Style</h2>
                      <p className="text-sm text-muted-foreground">
                        Choose between bold logo impact or technical documentation
                      </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {OUTPUT_PROFILES.map((profile) => (
                        <button
                          key={profile.value}
                          onClick={() => setOutputProfile(profile.value)}
                          className={`glass-card p-6 text-left transition-all duration-300 ${
                            outputProfile === profile.value 
                              ? 'border-primary ring-2 ring-primary/20' 
                              : 'hover:border-primary/50'
                          }`}
                          data-testid={`profile-${profile.value}`}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              outputProfile === profile.value 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-primary/10 text-primary'
                            }`}>
                              {profile.icon}
                            </div>
                            <h3 className="font-heading text-lg">{profile.label}</h3>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">{profile.description}</p>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            {profile.features.map((feature, i) => (
                              <li key={i} className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-primary/50"></span>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </button>
                      ))}
                    </div>

                    {/* Advanced Stroke Styling */}
                    <div className="glass-card p-5 mt-4">
                      <h3 className="font-heading text-sm mb-4 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        Stroke Styling (Advanced)
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-muted-foreground mb-2 block">Line Ends</Label>
                          <Select value={strokeLinecap} onValueChange={setStrokeLinecap}>
                            <SelectTrigger className="h-9" data-testid="stroke-linecap-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STROKE_LINECAPS.map((cap) => (
                                <SelectItem key={cap.value} value={cap.value}>
                                  <span className="flex items-center gap-2">
                                    <span className="font-medium">{cap.label}</span>
                                    <span className="text-xs text-muted-foreground">- {cap.description}</span>
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-2 block">Line Joins</Label>
                          <Select value={strokeLinejoin} onValueChange={setStrokeLinejoin}>
                            <SelectTrigger className="h-9" data-testid="stroke-linejoin-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STROKE_LINEJOINS.map((join) => (
                                <SelectItem key={join.value} value={join.value}>
                                  <span className="flex items-center gap-2">
                                    <span className="font-medium">{join.label}</span>
                                    <span className="text-xs text-muted-foreground">- {join.description}</span>
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Final Summary */}
                <div className="glass-card p-6 bg-primary/5">
                  <h3 className="font-heading text-lg mb-4">Creation Summary</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <p><span className="text-muted-foreground">Intention:</span> <span className="font-medium capitalize">{intention || 'Not set'}</span></p>
                      <p><span className="text-muted-foreground">Tradition:</span> <span className="font-medium capitalize">{tradition.replace('_', ' ')}</span></p>
                      <p><span className="text-muted-foreground">Geometry:</span> <span className="font-medium capitalize">{geometry.replace(/_/g, ' ')}</span></p>
                    </div>
                    <div className="space-y-2">
                      <p><span className="text-muted-foreground">Symmetry:</span> <span className="font-medium">{symmetry}-fold</span></p>
                      <p><span className="text-muted-foreground">Palette:</span> <span className="font-medium capitalize">{palette}</span></p>
                      <p><span className="text-muted-foreground">Mode:</span> <span className="font-medium capitalize">{generationMode === 'vector' ? `Vector - ${outputProfile === 'sigil' ? 'Sigil' : 'Blueprint'}` : 'AI Generated'}</span></p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons - Added bottom padding for mobile to avoid bug report button */}
          <div className="flex justify-between mt-10 pb-24 md:pb-10">
            <Button
              variant="outline"
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="rounded-full gap-2"
              data-testid="prev-step-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Back</span>
            </Button>

            {step < totalSteps ? (
              <Button
                onClick={() => setStep(s => Math.min(totalSteps, s + 1))}
                className="rounded-full gap-2"
                data-testid="next-step-btn"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <div className="relative group">
                <Button
                  onClick={handleGenerateClick}
                  disabled={isGenerating}
                  className={`rounded-full gap-2 ${!intention.trim() ? 'opacity-50 cursor-not-allowed' : 'glow-primary'}`}
                  data-testid="generate-btn"
                >
                  {generationMode === 'vector' ? <Zap className="w-4 h-4" /> : <Wand2 className="w-4 h-4" />}
                  <span className="hidden sm:inline">{generationMode === 'vector' ? 'Generate Vector Art' : 'Generate AI Art'}</span>
                  <span className="sm:hidden">Generate</span>
                  <Sparkles className="w-4 h-4" />
                </Button>
                {!intention.trim() && (
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-2 bg-destructive text-destructive-foreground text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    Please enter an intention on Step 1
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Terms of Use Modal */}
      <TermsOfUseModal
        isOpen={showTermsModal}
        onAccept={handleTermsAccepted}
        onDecline={handleTermsDeclined}
      />
    </div>
  );
};

export default Studio;
